
import { defineComponent } from 'vue'

// Define proper types for file input listeners
type FileInputEventListener = (event: Event) => void
type FileInputListeners = Record<string, FileInputEventListener | FileInputEventListener[]>

// More specific interface for what this component actually returns
interface FileInputEventListeners {
  [key: string]: FileInputEventListener | FileInputEventListener[]
  change: FileInputEventListener // change event is always present
}

export default defineComponent({
  name: 'ZFileInput',
  inheritAttrs: false,
  props: {
    processing: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: 'Add file'
    }
  },
  data() {
    return {
      uploadedFiles: [] as File[],
      prevFiles: [] as File[]
    }
  },
  computed: {
    uploadedFileNames(): string {
      const nameExtractor = (acc: string, file: File): string => {
        return acc + file.name + ', '
      }
      return this.uploadedFiles.reduce(nameExtractor, '').slice(0, -2)
    },
    fileInputListeners(): FileInputEventListeners {
      // Create a properly typed listeners object
      const typedListeners: FileInputListeners = {}

      // Copy existing listeners (Vue 2's $listeners might contain non-event functions, so we filter them)
      Object.entries(this.$listeners).forEach(([key, listener]) => {
        if (typeof listener === 'function') {
          typedListeners[key] = listener as FileInputEventListener
        } else if (Array.isArray(listener)) {
          typedListeners[key] = listener.filter(
            (l) => typeof l === 'function'
          ) as FileInputEventListener[]
        }
      })

      // Add our specific change handler (required by FileInputEventListeners)
      typedListeners.change = (event: Event) => {
        const fileList = (event.target as HTMLInputElement)?.files
        if (fileList) {
          this.prevFiles = this.uploadedFiles
          this.uploadedFiles = Array.from(fileList)
        }
        this.$emit('change', event)
      }

      // Type assertion is safe here since we ensure 'change' is present
      return typedListeners as FileInputEventListeners
    }
  },
  methods: {
    openFilePicker(): void {
      ;(this.$refs['fileUpload'] as HTMLInputElement).click()
    },
    keyboardToggle(event: KeyboardEvent) {
      if (event.code === 'Space' || event.code === 'Enter') {
        this.openFilePicker()
      }
    },
    emptyFiles(): void {
      this.prevFiles = this.uploadedFiles
      this.uploadedFiles = []
    },
    invalidateChanges(): void {
      this.uploadedFiles = this.prevFiles
      this.prevFiles = []
    },
    filesEmptied(): void {
      this.prevFiles = this.uploadedFiles
      this.uploadedFiles = []
      this.$emit('files-emptied')
    }
  }
})
